import { getSalesOverviewDimTrend } from '@/pages/dashboard/services/monetization';
import { formatFilterValues, otherParamsVal } from '@/pages/dashboard/utils/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IRequestParams } from '../SalesBreakdownCardService/type';
import moment from 'moment';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { useSelectArrObj } from '@/pages/dashboard/utils/customHook';
import { TColumnRender } from '@/pages/dashboard/components/EchartsTooltip/type';
import {
  IAccumulationMap,
  IChartLegendConfig,
  IChartOption,
  ICustomAccumulationItem,
  IDimensionDataItem,
  IDimensionTrendResponse,
  IFilterCatalogItem,
  IFilterValue,
  ISeparatedOption,
  IUseDimensionDataProps,
  IUseDimensionDataReturn,
} from './type';

const useDimensionData = (
  props: IUseDimensionDataProps,
): IUseDimensionDataReturn => {
  const { gameCode, filterVal, selectedMetricId, otherParams: otherParamsData, customAccumulation, fixedDimensionValue } = props;
  const [data, setData] = useState<IDimensionDataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);
  const [dimensionValue, setDimensionValue] = useState<string>(fixedDimensionValue || '');
  const requestVersionRef = useRef<number>(0);

  const { filterDefaultSelect, pagePath } = filterVal || {}; // 接口筛选器
  let SEPARATED_OPTIONS = useSelectArrObj(filterVal, false, false, filterDefaultSelect, pagePath, true); // 维度选项
  SEPARATED_OPTIONS = (SEPARATED_OPTIONS || []).filter(
    (item: ISeparatedOption) => item?.groupKey === 'separated',
  );
  const separatedOptionsLength = SEPARATED_OPTIONS?.length || 0;

  // 转化维度查询条件
  const getDimension = (value: string): IAccumulationMap => {
    const accumulation: IAccumulationMap = {};

    if (customAccumulation?.length) {
      // 自定义 accumulation 模式：所有现有维度全部设为 false
      SEPARATED_OPTIONS?.forEach(
        (item: ISeparatedOption) =>
          (accumulation[`by_${item.value}`] = false),
      );
      // 新增 custom_accumulation 字段
      accumulation.custom_accumulation = customAccumulation;
    } else {
      // 默认模式：仅选中维度为 true
      SEPARATED_OPTIONS?.forEach((item: ISeparatedOption) => {
        accumulation[`by_${item.value}`] = item.value === value;
      });
    }

    return accumulation;
  };

  useEffect(() => {
    const canRequest = separatedOptionsLength > 0 && gameCode && !!dimensionValue;

    if (canRequest) {
      const { timeType, rangeTime, ...filters } = filterVal || {};
      const periodKey = (timeType || 'Day') as keyof typeof PERIODTIMEOBJ;
      const filtersVal = formatFilterValues(filters);
      const otherParams = otherParamsVal(otherParamsData, filtersVal) || {};

      const params: IRequestParams = {
        game_code: gameCode,
        start_date: moment(rangeTime?.[0]).format('YYYY-MM-DD'),
        end_date: moment(rangeTime?.[1]).format('YYYY-MM-DD'),
        period: PERIODTIMEOBJ[periodKey],
        filters: filtersVal,
        metrics: [selectedMetricId],
        accumulation: getDimension(dimensionValue),
        ...(otherParams || {})
      };

      requestVersionRef.current += 1;
      const currentVersion = requestVersionRef.current;
      // 标记当前参数组合已经发起过维度请求，后续空数组也要按真实结果处理。
      setHasRequested(true);
      setLoading(true);
      setIsError(false);

      getSalesOverviewDimTrend(params)
        .then((response: IDimensionTrendResponse) => {
          if (currentVersion !== requestVersionRef.current) {
            return;
          }
          setData(response?.trends || []);
          setLoading(false);
        })
        .catch(() => {
          if (currentVersion !== requestVersionRef.current) {
            return;
          }
          setData([]);
          setLoading(false);
          setIsError(true);
        });
    }
  }, [dimensionValue, gameCode, filterVal, selectedMetricId, separatedOptionsLength]);

  return {
    SEPARATED_OPTIONS,
    dimensionData: data,
    dimensionValue,
    loading,
    isError,
    hasRequested,
    setDimensionValue,
  };
};

interface IUseCatalogIconProps {
  filterVal: IFilterValue;
  customAccumulation?: ICustomAccumulationItem[];
}

interface IUseCatalogIconReturn {
  hasCatalogIcon: boolean;
  catalogNameRender: TColumnRender;
  getCatalogLegend: (opt: IChartOption) => void;
}

export const useCatalogIcon = (props: IUseCatalogIconProps): IUseCatalogIconReturn => {
  const { filterVal, customAccumulation } = props;

  // 构建 catalog 维度的 image 映射表（value → image）
  const catalogImageMap = useMemo(() => {
    const map: Record<string, string> = {};
    const catalogList = filterVal?.filterCatalog?.list || [];
    catalogList.forEach((item: IFilterCatalogItem) => {
      if (item?.value && item?.image) {
        map[item.value] = item.image;
        // 同时用 nameEn 和 nameCn 作为 key，方便通过图例名称匹配
        if (item.nameEn) map[item.nameEn] = item.image;
        if (item.nameCn) map[item.nameCn] = item.image;
      }
    });
    return map;
  }, [filterVal?.filterCatalog]);

  // 根据维度名称获取 catalog 图标
  const getCatalogImage = (name: string): string => {
    return catalogImageMap[name] || catalogImageMap[name?.trim()] || '';
  };

  const getCatalogLegendImage = (name: string): string => {
    return getCatalogImage(name?.trim());
  };

  // 是否使用 catalog 图标（配置了 customAccumulation 且 filterCatalog 有数据）
  const hasCatalogIcon = !!(customAccumulation?.length && Object.keys(catalogImageMap).length > 0);

  // 自定义浮窗 name 列渲染：带 catalog 图标
  const catalogNameRender: TColumnRender = (value, row, _column, icon) => {
    const catalogIcon = getCatalogImage(value);
    return (
      <div className="dbd-custom-icon">
        {icon}
        {!['sum', 'special'].includes(row?._key) && catalogIcon && (
          <span>
            <img className="dbd-catalog-icon" src={catalogIcon} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          </span>
        )}
        <span>{value}</span>
      </div>
    );
  };

  // 为 catalog 维度的图例添加图片图标（类似 getSystemLegend）
  const getCatalogLegend = (opt: IChartOption) => {
    try {
      const legendDataList = opt.legend?.data;
      if (!legendDataList?.length) return;
      const legendRich: NonNullable<IChartLegendConfig['textStyle']>['rich'] = {};
      const legendMap: Record<string, string> = {};
      const xArr = Array(legendDataList.length + 11)
        .fill(0)
        .map((_, i: number) => `icon${i}`);

      legendDataList.forEach((legend: string | { name: string; }, index: number) => {
        const legendName = typeof legend === 'string' ? legend : legend?.name;
        const legendImage = getCatalogLegendImage(legendName);
        const richKey = xArr[index];
        if (legendName && legendImage && richKey) {
          legendMap[legendName] = richKey;
          legendRich[richKey] = {
            backgroundColor: {
              image: legendImage,
            },
          };
        }
      });

      // eslint-disable-next-line no-undefined
      const { undefined: unusedKey, ...newLegendRich } = legendRich;
      // eslint-disable-next-line no-param-reassign
      opt.legend = {
        ...opt.legend,
        data: opt.legend?.data?.map((legendItem: string | { name: string; }) => ({
          name: typeof legendItem === 'string' ? legendItem : legendItem?.name,
          icon: 'circle',
        })),
        formatter: (name: string) => {
          const richName = legendMap[name?.trim()];
          if (richName) {
            return `{${richName}| } ${name}`;
          }
          return name;
        },
        textStyle: {
          ...opt.legend?.textStyle,
          color: '#838697',
          rich: newLegendRich,
        },
      };
    } catch (error) {
      console.error(error);
    }
  };

  return {
    hasCatalogIcon,
    catalogNameRender,
    getCatalogLegend,
  };
};

export default useDimensionData;
